import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import MultiSelectDropdown from './MultiSelectDropdown';

const BulkAssignmentModal = ({
  isOpen,
  onClose,
  selectedUsers,
  onAssignUsers,
  filterOptions, // Use filterOptions from parent component
  currentUser, // Add currentUser to check role
}) => {
  const [assignmentData, setAssignmentData] = useState({
    schools: [],
    courses: [],
    classes: [],
    instructors: [], // Add instructors to assignment data
  });
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Get classes for selected courses (reuse existing logic)
  const getClassesForSelectedCourses = () => {
    const selectedCourseIds = assignmentData.courses;
    const allClasses = [];

    selectedCourseIds.forEach((courseId) => {
      const course = filterOptions.courses.find((c) => c._id === courseId);
      if (course && course.classes) {
        course.classes.forEach((cls) => {
          allClasses.push({
            value: cls._id,
            label: `${course.name} - ${cls.name}`,
            courseId: courseId,
            courseName: course.name,
            className: cls.name,
          });
        });
      }
    });

    return allClasses;
  };

  const handleAssignmentChange = (type, values) => {
    setAssignmentData((prev) => ({
      ...prev,
      [type]: values,
    }));

    // Reset classes when courses change
    if (type === 'courses') {
      setAssignmentData((prev) => ({
        ...prev,
        classes: [],
      }));
    }
  };

  const hasAnyAssignment = () => {
    return (
      assignmentData.schools.length > 0 ||
      assignmentData.courses.length > 0 ||
      assignmentData.classes.length > 0 ||
      assignmentData.instructors.length > 0
    );
  };

  const handleSubmit = async () => {
    if (!hasAnyAssignment()) {
      toast({
        title: 'Validation Error',
        description:
          'Please select at least one school, course, class, or instructor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Prepare assignment data with selected user IDs
    const assignmentPayload = {
      ...assignmentData,
      userIds: selectedUsers.map((user) => user.userId).filter(Boolean),
    };

    setLoading(true);
    try {
      await onAssignUsers(assignmentPayload);
      handleClose();
    } catch (error) {
      console.error('Error in assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAssignmentData({
        schools: [],
        courses: [],
        classes: [],
        instructors: [],
      });
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay bg="#53535399" />
      <ModalContent bg={bgColor} borderColor={borderColor} borderWidth="1px">
        <ModalHeader>Assign Users to Academic Programs</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Assign {selectedUsers.length} selected users to schools, courses,
            classes, and instructors
          </Text>

          <VStack spacing={4} align="stretch">
            <MultiSelectDropdown
              label="Schools"
              options={filterOptions.schools.map((school) => ({
                value: school._id,
                label: school.name,
              }))}
              selectedValues={assignmentData.schools}
              onChange={(values) => handleAssignmentChange('schools', values)}
              placeholder="Select schools"
              width="100%"
            />

            <MultiSelectDropdown
              label="Courses"
              options={filterOptions.courses.map((course) => ({
                value: course._id,
                label: course.name,
              }))}
              selectedValues={assignmentData.courses}
              onChange={(values) => handleAssignmentChange('courses', values)}
              placeholder="Select courses"
              width="100%"
            />

            {assignmentData.courses.length > 0 && (
              <MultiSelectDropdown
                label="Classes"
                options={getClassesForSelectedCourses()}
                selectedValues={assignmentData.classes}
                onChange={(values) => handleAssignmentChange('classes', values)}
                placeholder="Select classes"
                width="100%"
              />
            )}

            {/* Instructors dropdown - only show for admin and super_admin */}
            {(currentUser?.role?.toLowerCase() === 'admin' ||
              currentUser?.role?.toLowerCase() === 'super_admin') && (
              <MultiSelectDropdown
                label="Instructors"
                options={filterOptions.instructors.map((instructor) => ({
                  value: instructor.email,
                  label: instructor.fullName,
                }))}
                selectedValues={assignmentData.instructors}
                onChange={(values) =>
                  handleAssignmentChange('instructors', values)
                }
                placeholder="Select instructors"
                width="100%"
              />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="outline"
              onClick={handleClose}
              borderRadius="8px"
              borderColor="#AAAAAA"
              height="36px"
              fontSize="14px"
              fontWeight="700"
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              bg="#EF6506"
              color="white"
              onClick={handleSubmit}
              borderRadius="8px"
              height="36px"
              fontSize="14px"
              fontWeight="700"
              _hover="none"
              isLoading={loading}
              isDisabled={!hasAnyAssignment()}
            >
              Assign Users
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkAssignmentModal;
